import { ExIcon, ExMenu, ExMenuItem, MenuItemVariant } from '@boomi/exosphere';
import { useCallback } from 'react';
import { useEffect, useRef } from 'react';
import { translations } from '../translations/translations';
import { useApp } from './AppProvider';
import { boomiNavigationMenuLinks } from './BoomiNavigationMenuLinks';

const BoomiNavigationMenu = () => {
    const { language } = useApp();
    const { platform, products, labs } = boomiNavigationMenuLinks(language);

    const boomiNavigationMenuRef = useRef<HTMLDetailsElement>(null);

    const closeOpenMenu = useCallback(() => {
        if (boomiNavigationMenuRef.current?.hasAttribute('open')) {
            boomiNavigationMenuRef.current.removeAttribute('open');
        }
    }, []);

    // Close the open menu if clicking elsewhere on the page.
    useEffect(() => {
        const handleMenuOutsideClicks = (event: MouseEvent) => {
            if (
                !(
                    (event.target as Element).classList.contains('boomi-navigation-trigger') ||
                    (event.target as Element).classList.contains('boomi-navigation-wrapper')
                )
            ) {
                closeOpenMenu();
            }
        };
        document.addEventListener('click', handleMenuOutsideClicks);

        return () => {
            document.removeEventListener('click', handleMenuOutsideClicks);
        };
    }, [closeOpenMenu]);

    return (
        <details ref={boomiNavigationMenuRef} className="boomi-navigation-wrapper">
            <summary
                className="automate-link boomi-navigation-trigger"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.key === 'Escape' && closeOpenMenu()}
            >
                <ExIcon icon="Dotsnine" className="utility-icon" hide-browser-tooltip />
            </summary>
            <ExMenu
                className="boomi-navigation-menu custom-scrollbars"
                onKeyDown={(e) => e.key === 'Escape' && closeOpenMenu()}
            >
                <span className="menu-heading">{translations[language].BOOMI_HEADER_platform}</span>
                {platform.map((item) => (
                    <a
                        key={item.title}
                        className="menu-item-link"
                        href={item.link}
                        target="_blank"
                        rel="noreferrer noopener"
                        tabIndex={-1}
                    >
                        <ExMenuItem className="menu-item" variant={MenuItemVariant.NAVIGATION}>
                            <ExIcon className="menu-item-icon" icon={item.icon} />
                            {item.title}
                        </ExMenuItem>
                    </a>
                ))}
                <hr className="menu-separator" />
                <span className="menu-heading">{translations[language].BOOMI_HEADER_products}</span>
                {products.map((item) => (
                    <a
                        key={item.title}
                        className="menu-item-link"
                        href={item.link}
                        target="_blank"
                        rel="noreferrer noopener"
                        tabIndex={-1}
                    >
                        <ExMenuItem className="menu-item" variant={MenuItemVariant.NAVIGATION}>
                            <ExIcon className="menu-item-icon" icon={item.icon} />
                            {item.title}
                        </ExMenuItem>
                    </a>
                ))}
                <hr className="menu-separator" />
                <details
                    className="sub-menu"
                    onBlur={(e) => {
                        // auto-close the sub-menu when it loses focus and
                        // no child items are focused
                        const el = e.currentTarget;
                        if (!el.matches(':focus-within:not(:focus)')) {
                            setTimeout(() => {
                                el.removeAttribute('open');
                            }, 300);
                        }
                    }}
                >
                    {/* Stop the click from propagating so the ExDropdown stays open. */}
                    {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                    <summary onClick={(e) => e.stopPropagation()}>
                        <ExIcon className="menu-item-icon" icon="Lab" />
                        <span className="menu-heading">
                            {translations[language].BOOMI_HEADER_labs}
                        </span>
                        <ExIcon className="menu-indicator" icon="Down caret" />
                    </summary>
                    {labs.map((item) => (
                        <a
                            key={item.title}
                            className="menu-item-link"
                            href={item.link}
                            target="_blank"
                            rel="noreferrer noopener"
                            tabIndex={-1}
                        >
                            <ExMenuItem className="menu-item" variant={MenuItemVariant.NAVIGATION}>
                                {item.title}
                            </ExMenuItem>
                        </a>
                    ))}
                </details>
            </ExMenu>
        </details>
    );
};

export default BoomiNavigationMenu;
